import React from 'react';
import { BarChart3, TrendingUp, TrendingDown, Activity, Filter, ArrowUp, ArrowDown } from 'lucide-react';
import './SalesTrendsChart.css';

function SalesTrendsChart({ orders, timeFilter, onTimeFilterChange }) {
  const getFilteredData = (filter) => {
    const now = new Date();
    const filteredOrders = orders.filter(order => {
      if (!order.createdAt) return false;
      const orderDate = order.createdAt.toDate();
      
      switch (filter) {
        case 'daily':
          return orderDate.toDateString() === now.toDateString();
        case 'weekly':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return orderDate >= weekAgo;
        case 'monthly':
          return orderDate.getMonth() === now.getMonth() && 
                 orderDate.getFullYear() === now.getFullYear();
        case 'yearly':
          return orderDate.getFullYear() === now.getFullYear();
        default:
          return true;
      }
    });

    return filteredOrders;
  };

  const getSalesTrends = () => {
    const filteredOrders = getFilteredData(timeFilter);
    const salesData = [];
    
    // Generate time-based data points
    const now = new Date();
    let dataPoints = [];
    let labelFormat = '';
    
    switch (timeFilter) {
      case 'daily':
        // Hourly data for today
        for (let i = 0; i < 24; i++) {
          dataPoints.push({
            label: i.toString().padStart(2, '0') + ':00',
            timestamp: new Date(now.getFullYear(), now.getMonth(), now.getDate(), i, 0, 0)
          });
        }
        break;
      case 'weekly':
        // Daily data for this week
        const weekStart = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
        for (let i = 0; i < 7; i++) {
          const date = new Date(weekStart.getTime() + i * 24 * 60 * 60 * 1000);
          dataPoints.push({
            label: date.toLocaleDateString('en-US', { weekday: 'short' }),
            timestamp: date
          });
        }
        break;
      case 'monthly':
        // Daily data for this month
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        for (let i = 1; i <= daysInMonth; i++) {
          dataPoints.push({
            label: i.toString(),
            timestamp: new Date(now.getFullYear(), now.getMonth(), i, 0, 0, 0)
          });
        }
        break;
      case 'yearly':
        // Monthly data for this year
        for (let i = 0; i < 12; i++) {
          dataPoints.push({
            label: new Date(now.getFullYear(), i, 1).toLocaleDateString('en-US', { month: 'short' }),
            timestamp: new Date(now.getFullYear(), i, 1, 0, 0, 0)
          });
        }
        break;
    }

    // Calculate sales for each data point
    dataPoints.forEach(point => {
      const pointSales = filteredOrders
        .filter(order => {
          const orderDate = order.createdAt.toDate();
          return orderDate >= point.timestamp && 
                 (timeFilter === 'daily' ? orderDate < new Date(point.timestamp.getTime() + 60 * 60 * 1000) :
                  timeFilter === 'weekly' ? orderDate < new Date(point.timestamp.getTime() + 24 * 60 * 60 * 1000) :
                  timeFilter === 'monthly' ? orderDate < new Date(point.timestamp.getTime() + 24 * 60 * 60 * 1000) :
                  orderDate < new Date(point.timestamp.getFullYear(), point.timestamp.getMonth() + 1, 1));
        })
        .reduce((sum, order) => sum + (order.total || 0), 0);

      salesData.push({
        label: point.label,
        sales: pointSales,
        orders: filteredOrders.filter(order => {
          const orderDate = order.createdAt.toDate();
          return orderDate >= point.timestamp && 
                 (timeFilter === 'daily' ? orderDate < new Date(point.timestamp.getTime() + 60 * 60 * 1000) :
                  timeFilter === 'weekly' ? orderDate < new Date(point.timestamp.getTime() + 24 * 60 * 60 * 1000) :
                  timeFilter === 'monthly' ? orderDate < new Date(point.timestamp.getTime() + 24 * 60 * 60 * 1000) :
                  orderDate < new Date(point.timestamp.getFullYear(), point.timestamp.getMonth() + 1, 1));
        }).length
      });
    });

    return salesData;
  };

  const getGrowthMetrics = () => {
    const salesData = getSalesTrends();
    if (salesData.length < 2) return { growth: 0, trend: 'stable' };

    const recent = salesData.slice(-3).reduce((sum, d) => sum + d.sales, 0);
    const previous = salesData.slice(-6, -3).reduce((sum, d) => sum + d.sales, 0);
    
    if (previous === 0) return { growth: 0, trend: 'stable' };
    
    const growth = ((recent - previous) / previous) * 100;
    return {
      growth: growth.toFixed(1),
      trend: growth > 5 ? 'up' : growth < -5 ? 'down' : 'stable'
    };
  };

  const salesData = getSalesTrends();
  const growthMetrics = getGrowthMetrics();
  const maxSales = Math.max(...salesData.map(d => d.sales), 1);
  const totalSales = salesData.reduce((sum, d) => sum + d.sales, 0);
  const totalOrders = salesData.reduce((sum, d) => sum + d.orders, 0);

  return (
    <div className="sales-trends-chart">
      <div className="chart-header">
        <h3>Sales Trends Analysis</h3>
        <div className="chart-time-filter">
          <Filter size={16} />
          <select value={timeFilter} onChange={(e) => onTimeFilterChange(e.target.value)}>
            <option value="daily">Today</option>
            <option value="weekly">This Week</option>
            <option value="monthly">This Month</option>
            <option value="yearly">This Year</option>
          </select>
        </div>
      </div>

      <div className="trends-overview">
        <div className="overview-cards">
          <div className="trend-card">
            <div className="trend-icon sales">
              <BarChart3 size={24} />
            </div>
            <div className="trend-content">
              <h4>Total Sales</h4>
              <p>${totalSales.toFixed(2)}</p>
              <div className={`trend-indicator ${growthMetrics.trend}`}>
                {growthMetrics.trend === 'up' ? <ArrowUp size={16} /> : 
                 growthMetrics.trend === 'down' ? <ArrowDown size={16} /> : 
                 <Activity size={16} />}
                <span>{growthMetrics.growth}%</span>
              </div>
            </div>
          </div>

          <div className="trend-card">
            <div className="trend-icon orders">
              <Activity size={24} />
            </div>
            <div className="trend-content">
              <h4>Total Orders</h4>
              <p>{totalOrders}</p>
              <div className="trend-indicator stable">
                <span>Period total</span>
              </div>
            </div>
          </div>

          <div className="trend-card">
            <div className="trend-icon average">
              <TrendingUp size={24} />
            </div>
            <div className="trend-content">
              <h4>Average Sale</h4>
              <p>${totalOrders > 0 ? (totalSales / totalOrders).toFixed(2) : '0.00'}</p>
              <div className="trend-indicator stable">
                <span>Per order</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="trends-visualization">
        <div className="chart-container">
          <div className="chart-header-info">
            <h4>Sales Performance Over Time</h4>
            <div className="chart-legend">
              <div className="legend-item">
                <div className="legend-color sales"></div>
                <span>Sales Revenue</span>
              </div>
              <div className="legend-item">
                <div className="legend-color orders"></div>
                <span>Order Count</span>
              </div>
            </div>
          </div>
          
          <div className="dual-chart">
            <div className="sales-chart">
              <div className="chart-grid">
                {salesData.map((data, index) => (
                  <div key={data.label} className="chart-bar-container">
                    <div className="bar-wrapper">
                      <div 
                        className="bar sales-bar"
                        style={{ 
                          height: `${(data.sales / maxSales) * 100}%`,
                          background: `linear-gradient(135deg, #dc2626 0%, #000000 100%)`
                        }}
                      ></div>
                    </div>
                    <span className="bar-label">{data.label}</span>
                    {data.sales > 0 && (
                      <span className="bar-value sales-value">${data.sales}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="orders-chart">
              <div className="chart-grid">
                {salesData.map((data, index) => {
                  const maxOrders = Math.max(...salesData.map(d => d.orders), 1);
                  return (
                    <div key={data.label} className="chart-bar-container">
                      <div className="bar-wrapper">
                        <div 
                          className="bar orders-bar"
                          style={{ 
                            height: `${(data.orders / maxOrders) * 100}%`,
                            background: `linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)`
                          }}
                        ></div>
                      </div>
                      <span className="bar-label">{data.label}</span>
                      {data.orders > 0 && (
                        <span className="bar-value orders-value">{data.orders}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="trends-insights">
        <h4>Key Insights</h4>
        <div className="insights-grid">
          <div className="insight-card">
            <div className="insight-icon peak">
              <TrendingUp size={20} />
            </div>
            <div className="insight-content">
              <h5>Peak Performance</h5>
              <p>Best period: {salesData.reduce((max, d) => d.sales > max.sales ? d : max, salesData[0])?.label || 'N/A'}</p>
              <span className="insight-value">
                ${Math.max(...salesData.map(d => d.sales)).toFixed(2)}
              </span>
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-icon growth">
              {growthMetrics.trend === 'up' ? <TrendingUp size={20} /> : 
               growthMetrics.trend === 'down' ? <TrendingDown size={20} /> : 
               <Activity size={20} />}
            </div>
            <div className="insight-content">
              <h5>Growth Trend</h5>
              <p>Recent performance</p>
              <span className={`insight-value ${growthMetrics.trend}`}>
                {growthMetrics.growth}%
              </span>
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-icon consistency">
              <BarChart3 size={20} />
            </div>
            <div className="insight-content">
              <h5>Consistency</h5>
              <p>Active periods</p>
              <span className="insight-value">
                {salesData.filter(d => d.sales > 0).length}/{salesData.length}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalesTrendsChart;
